import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { previewHotelInvoice } from "../../../../../store/actions"; 

export const useFinalBillPreview = (bookingDetails, hasFinalBill) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [openPreview, setOpenPreview] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [previewData, setPreviewData] = useState(null);

  const navigateToFinalBill = useCallback(() => {
    if (bookingDetails?.invoice_id) {
      navigate(`/billing/bookings/${bookingDetails.invoice_id}`);
    }
  }, [navigate, bookingDetails?.invoice_id]);

  const handlePreview = useCallback(async () => {
    // 1. If Final Bill Exists -> Redirect immediately
    if (hasFinalBill) {
      navigateToFinalBill();
      return;
    }

    // 2. Prevent duplicate calls
    if (loading) return;

    setLoading(true);
    setError(null);
    setOpenPreview(true); // Open modal to show loading state

    try {
      const result = await dispatch(
        previewHotelInvoice({
          booking_id: bookingDetails?.booking_id,
        })
      );

      const correctedTotals = recalculateTotalsFromRoomCharges(
        result.room_charges || []
      );

      if (result) {
        setPreviewData({
          ...result,
          ...correctedTotals,
        });
      } else {
        throw new Error("Received empty data from server.");
      }
    } catch (err) {
      console.error("Preview Error:", err);
      setError(err?.response?.data?.message || err?.message || "Failed to generate preview.");
    } finally {
      setLoading(false);
    }
  }, [hasFinalBill, loading, bookingDetails?.booking_id, dispatch, navigateToFinalBill]);

  const closePreview = useCallback(() => {
    setOpenPreview(false);
    // Cleanup state after transition to prevent UI flickering
    setTimeout(() => {
      setPreviewData(null);
      setError(null);
    }, 300);
  }, []);

  const retryPreview = useCallback(() => {
    closePreview();
    setTimeout(() => {
        handlePreview();
    }, 350);
  }, [handlePreview, closePreview]);

  return {
    openPreview,
    loading,
    previewData,
    error,
    handlePreview,
    closePreview,
    retryPreview,
    navigateToFinalBill,
  };
};

const recalculateTotalsFromRoomCharges = (roomCharges) => {
  let amount = 0;
  let sgst = 0;
  let cgst = 0;

  roomCharges.forEach((item) => {
    const isTaxRow = (item.sgst || 0) > 0 || (item.cgst || 0) > 0;

    if (!isTaxRow) {
      amount += item.amount || 0;
    }

    sgst += item.sgst || 0;
    cgst += item.cgst || 0;
  });

  return {
    amount: parseFloat(amount.toFixed(2)),
    sgst: parseFloat(sgst.toFixed(2)),
    cgst: parseFloat(cgst.toFixed(2)),
    total: parseFloat((amount + sgst + cgst).toFixed(2)),
  };
};